import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Settings, Mail, Save, Loader2, CheckCircle2, Eye, EyeOff, Link2 } from 'lucide-react';

export default function AdminConfiguracion() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { usuario: currentUser } = useAuth();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const [secretStatus, setSecretStatus] = useState({ set: false, hint: '' });
  const [tokenStatus, setTokenStatus] = useState({ set: false, hint: '' });

  const [showSecret, setShowSecret] = useState(false);

  // Handle OAuth callback from Google
  useEffect(() => {
    const code = searchParams.get('code');
    const returnedState = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      toast({
        title: 'Error de autorizacion',
        description: `Google devolvio un error: ${error}`,
        variant: 'destructive',
      });
      setSearchParams({}, { replace: true });
      return;
    }

    if (code) {
      const savedState = sessionStorage.getItem('gmail_oauth_state');
      sessionStorage.removeItem('gmail_oauth_state');

      if (!savedState || savedState !== returnedState) {
        toast({
          title: 'Error de seguridad',
          description: 'El parametro state no coincide. Intente de nuevo.',
          variant: 'destructive',
        });
        setSearchParams({}, { replace: true });
        return;
      }

      handleExchangeCode(code);
      setSearchParams({}, { replace: true });
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const config = await api.getGmailConfig();
      setClientId(config.client_id);
      setSecretStatus(config.client_secret);
      setTokenStatus(config.refresh_token);
    } catch (err) {
      toast({
        title: 'Error cargando configuracion',
        description: err instanceof Error ? err.message : 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const data: Record<string, string> = {};
      if (clientId) data.client_id = clientId;
      if (clientSecret) data.client_secret = clientSecret;

      const result = await api.updateGmailConfig(data);
      toast({
        title: 'Configuracion guardada',
        description: result.message,
      });

      setClientSecret('');
      await loadConfig();
    } catch (err) {
      toast({
        title: 'Error guardando configuracion',
        description: err instanceof Error ? err.message : 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConnectGmail = () => {
    if (!clientId) {
      toast({
        title: 'Client ID requerido',
        description: 'Guarde el Client ID antes de conectar Gmail.',
        variant: 'destructive',
      });
      return;
    }

    const state = crypto.randomUUID();
    sessionStorage.setItem('gmail_oauth_state', state);

    const redirectUri = `${window.location.origin}/admin/configuracion`;

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'https://mail.google.com/',
      access_type: 'offline',
      prompt: 'consent',
      state: state,
    });

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const handleExchangeCode = async (code: string) => {
    setIsConnecting(true);
    try {
      const redirectUri = `${window.location.origin}/admin/configuracion`;
      const result = await api.exchangeGmailCode(code, redirectUri);
      toast({
        title: 'Gmail conectado',
        description: result.message,
      });
      await loadConfig();
    } catch (err) {
      toast({
        title: 'Error al conectar Gmail',
        description: err instanceof Error ? err.message : 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    try {
      const result = await api.testGmailConnection();
      toast({
        title: 'Conexion exitosa',
        description: result.message,
      });
    } catch (err) {
      toast({
        title: 'Error de conexion',
        description: err instanceof Error ? err.message : 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setIsTesting(false);
    }
  };

  if (currentUser?.rol !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Acceso denegado</h2>
          <p className="text-muted-foreground mt-2">Solo los administradores pueden acceder a esta pagina.</p>
          <Button onClick={() => navigate('/')} className="mt-4">Volver al Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Settings className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">Configuracion</h1>
                <p className="text-sm text-muted-foreground">Ajustes de la plataforma</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Gmail API Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-primary" />
              <div>
                <h2 className="text-lg font-semibold">Gmail API</h2>
                <p className="text-sm text-muted-foreground">
                  Credenciales OAuth2 para sincronizar facturas desde Gmail
                </p>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="p-6 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="p-6 space-y-5">
              {/* Client ID */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Client ID</label>
                <Input
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="123456789-abcdef.apps.googleusercontent.com"
                  className="text-foreground bg-white"
                />
              </div>

              {/* Client Secret */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Client Secret
                  {secretStatus.set && (
                    <span className="ml-2 text-xs text-green-600 font-normal">
                      <CheckCircle2 className="h-3 w-3 inline mr-1" />
                      Configurado ({secretStatus.hint})
                    </span>
                  )}
                </label>
                <div className="relative">
                  <Input
                    type={showSecret ? 'text' : 'password'}
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder={secretStatus.set ? 'Dejar vacio para mantener el actual' : 'GOCSPX-...'}
                    className="text-foreground bg-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Save Button */}
              <div className="pt-1">
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {isSaving ? 'Guardando...' : 'Guardar credenciales'}
                </Button>
              </div>

              {/* Divider */}
              <div className="border-t pt-5">
                <label className="text-sm font-medium">
                  Conexion Gmail
                  {tokenStatus.set && (
                    <span className="ml-2 text-xs text-green-600 font-normal">
                      <CheckCircle2 className="h-3 w-3 inline mr-1" />
                      Conectado ({tokenStatus.hint})
                    </span>
                  )}
                </label>

                {tokenStatus.set ? (
                  <div className="mt-2 space-y-3">
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                      Gmail esta conectado. El refresh token se obtuvo automaticamente.
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleConnectGmail}
                        disabled={isConnecting || !clientId}
                      >
                        <Link2 className="h-4 w-4 mr-2" />
                        Reconectar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleTestConnection}
                        disabled={isTesting}
                      >
                        {isTesting ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4 mr-2" />
                        )}
                        {isTesting ? 'Probando...' : 'Probar conexion'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 space-y-3">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                      Gmail no esta conectado. Guarde el Client ID y Client Secret, luego haga clic en "Conectar Gmail".
                    </div>
                    <Button
                      onClick={handleConnectGmail}
                      disabled={isConnecting || !clientId}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {isConnecting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Mail className="h-4 w-4 mr-2" />
                      )}
                      {isConnecting ? 'Conectando...' : 'Conectar Gmail'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Help text */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg text-sm text-muted-foreground space-y-2">
                <p className="font-medium text-foreground">Como configurar:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Crear un proyecto en Google Cloud Console</li>
                  <li>Habilitar la Gmail API</li>
                  <li>Configurar la pantalla de consentimiento OAuth</li>
                  <li>Crear credenciales OAuth 2.0 (tipo "Aplicacion web")</li>
                  <li>Agregar <code className="bg-gray-200 px-1 rounded">{window.location.origin}/admin/configuracion</code> como URI de redireccionamiento autorizado</li>
                  <li>Copiar el Client ID y Client Secret aqui y guardar</li>
                  <li>Hacer clic en "Conectar Gmail" para autorizar automaticamente</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
